import { useState } from "react";
import { useAuth } from "../AuthContext";
import { updateWorkspaceExperience } from "../../lib/workspace";

/** Both shells share the persisted workspace experience setting. */
export function ExperienceToggle() {
  const { me, refresh } = useAuth();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  if (!me || !["owner", "admin"].includes(me.role)) return null;
  const current = me.workspace.experience_mode ?? "classic";

  async function select(mode: "classic" | "briefing") {
    if (!me || busy || mode === current) return;
    setBusy(true);
    setError(null);
    try {
      await updateWorkspaceExperience(me.workspace.id, mode);
      await refresh();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Could not change the workspace view.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="relative shrink-0">
      <div
        role="group"
        aria-label="Workspace view"
        aria-busy={busy}
        title="Changes the view for this workspace"
        className="inline-flex rounded-lg border border-slate-200 bg-white p-0.5 text-xs"
      >
        {(
          [
            ["classic", "Classic"],
            ["briefing", "Brief"],
          ] as const
        ).map(([mode, label]) => (
          <button
            key={mode}
            type="button"
            aria-pressed={current === mode}
            disabled={busy}
            onClick={() => void select(mode)}
            className={`rounded-md px-2.5 py-1.5 font-medium transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-slate-600 disabled:opacity-50 ${
              current === mode ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"
            }`}
          >
            {label}
          </button>
        ))}
      </div>
      {error && (
        <p
          role="alert"
          className="absolute right-0 top-full z-50 mt-2 w-64 rounded-lg border border-rose-200 bg-white p-3 text-xs text-rose-700 shadow-lg"
        >
          {error}
        </p>
      )}
    </div>
  );
}
